const _excluded = ["className", "style", "modalProps", "size", "height", "show", "title", "message", "cancelButtonLabel", "confirmationButtonLabel", "confirmationButtonClassName", "backdrop", "disableModalExit", "isConfirmationButtonDisabled", "isConfirmationButtonAlert", "isOnConfirmInProgress", "hideOnConfirm", "noCancelButton", "onClose", "onConfirm"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import React from 'react';
import PropTypes from 'prop-types';
import Button from '@adsk/alloy-react-button';
import { noop } from '@adsk/alloy-react-helpers';
import { useLocale } from '@adsk/alloy-react-locale';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
import Modal from '../Modal';
/**
 * A modal component used for confirmation message.
 */
const ModalConfirmation = _ref => {
  let {
      className,
      style,
      modalProps,
      size = Modal.SIZES.SMALL,
      height = 'auto',
      show = true,
      title,
      message,
      cancelButtonLabel,
      confirmationButtonLabel,
      confirmationButtonClassName,
      backdrop = true,
      disableModalExit,
      isConfirmationButtonDisabled,
      isConfirmationButtonAlert = true,
      isOnConfirmInProgress,
      hideOnConfirm,
      noCancelButton,
      onClose,
      onConfirm = noop
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const {
    T
  } = useLocale(_ref2 => {
    let {
      locale
    } = _ref2;
    return require("../i18n/".concat(locale, ".json"));
  });
  disableModalExit = disableModalExit || backdrop === 'static';
  const onConfirmHide = e => {
    onConfirm && onConfirm(e);
    hideOnConfirm && onClose && onClose();
  };
  return /*#__PURE__*/React.createElement(Modal, _extends({
    "data-testid": "ModalConfirmation",
    className: className,
    style: style,
    open: show,
    hideOverlay: backdrop === false,
    onClose: onClose,
    size: size,
    height: height
  }, modalProps, props), title && /*#__PURE__*/React.createElement(Modal.Header, {
    "data-testid": "ModalConfirmationHeader",
    disableBorder: true
  }, title, !disableModalExit && /*#__PURE__*/React.createElement(Modal.Close, {
    onClick: onClose
  })), message && /*#__PURE__*/React.createElement(Modal.Body, {
    "data-testid": "ModalConfirmationBody"
  }, message), /*#__PURE__*/React.createElement(Modal.Footer, {
    "data-testid": "ModalConfirmationFooter",
    disableBorder: true
  }, !noCancelButton && /*#__PURE__*/React.createElement(Button, {
    "data-testid": "ModalConfirmationFooter__cancel-button",
    style: {
      marginRight: theme.spacing.XS
    },
    onClick: onClose
  }, cancelButtonLabel || T.translate('CANCEL')), /*#__PURE__*/React.createElement(Button, {
    "data-testid": "ModalConfirmationFooter__confirmation-button",
    className: confirmationButtonClassName,
    onClick: onConfirmHide,
    variant: Button.VARIANTS.PRIMARY,
    loading: isOnConfirmInProgress,
    disabled: isConfirmationButtonDisabled,
    alert: isConfirmationButtonAlert
  }, confirmationButtonLabel || T.translate('CONFIRM'))));
};
ModalConfirmation.SIZES = Modal.SIZES;
ModalConfirmation.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Modal size */
  size: PropTypes.oneOf(Object.values(ModalConfirmation.SIZES)),
  /** modal height, default is auto */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Allows to pass props directly to the Modal component
   */
  modalProps: PropTypes.object,
  /**
   * A property (along with `onClose`) making this component an uncontrolled one.
   * When this prop isn't passed the component is controlled.
   * Controls whether or not to show the modal.
   */
  show: PropTypes.bool,
  /**
   * A property (along with `show`) making this component an uncontrolled one.
   * When this prop isn't passed the component is controlled.
   * Callback to when hiding the modal.
   */
  onClose: PropTypes.func,
  /**
   * A callback function for the modal confirm event (when clicking on the confirmation button)
   */
  onConfirm: PropTypes.func,
  /**
   * Text or a node to be displayed in the header of the modal
   */
  title: PropTypes.node,
  /**
   * Text or a node to be displayed in the body of the modal
   */
  message: PropTypes.node,
  /**
   * Whether or not the confirmation has progress or not.
   */
  hideOnConfirm: PropTypes.bool,
  /**
   * Whether modal buttons should be disabled
   */
  isOnConfirmInProgress: PropTypes.bool,
  /**
   * Whether confirmation button should be disabled
   */
  isConfirmationButtonDisabled: PropTypes.bool,
  /**
   * Whether confirmation button should be alert (red)
   */
  isConfirmationButtonAlert: PropTypes.bool,
  /**
   * Text for confirmation button
   */
  confirmationButtonLabel: PropTypes.string,
  /**
   * A custom CSS class for confirmation button
   */
  confirmationButtonClassName: PropTypes.string,
  /**
   * Text for cancel button (if not passed then will default to 'Cancel' based on locale)
   */
  cancelButtonLabel: PropTypes.string,
  /**
   * Whether the cancel button should not be displayed
   */
  noCancelButton: PropTypes.bool,
  /**
   * Determines if the modal cannot be exited (exit button will be hidden and clickoutside will be disabled)
   */
  disableModalExit: PropTypes.bool,
  /**
   * The modal container component. Used for attaching the modal as a child component to the container.
   */
  container: PropTypes.element,
  /**
   * The locale to use for the default cancel button label
   */
  locale: PropTypes.string,
  /**
   * Determines the backdrop of modal.
   */
  backdrop: PropTypes.oneOf(['static', true, false])
};
export default ModalConfirmation;
//# sourceMappingURL=ModalConfirmation.js.map